import { useCallback } from "react";
import { writeXlsx } from "hucre/xlsx";

/**
 * Custom hook for exporting data to Excel with formatting using Hucre
 * @param {Array} exportConfig - Column configuration for export
 * @param {string} exportFilename - Base filename for the exported file
 * @returns {Function} exportToExcel - Function to export data to Excel
 */
export const useExportExcel = (exportConfig, exportFilename) => {
  const exportToExcel = useCallback(
    async (table) => {
      const tableRows = table.getFilteredRowModel().rows;
      if (!tableRows.length) return;

      const border = {
        top: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } },
      };
      const headerStyle = {
        font: { bold: true, size: 12 },
        alignment: { vertical: "center", horizontal: "left" },
        border,
      };

      const buffer = await writeXlsx({
        sheets: [
          {
            name: "Data",
            columns: exportConfig.map((col) => ({
              header: col.header,
              key: col.key,
              width: col.width || 20,
            })),
            rows: [
              exportConfig.map((col) => ({
                value: col.header,
                style: headerStyle,
              })),
              ...tableRows.map(({ original: data }) =>
                exportConfig.map((col) => ({
                  value: col.formatter
                    ? col.formatter(data)
                    : (data[col.key] ?? ""),
                  style: { border },
                })),
              ),
            ],
          },
        ],
      });

      const timestamp = new Date().toISOString().split("T")[0]; // YYYY-MM-DD
      const baseName = exportFilename || "ExportTransactions";
      const fullFileName = `${baseName}_${timestamp}.xlsx`;

      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fullFileName;
      link.click();
      window.URL.revokeObjectURL(url);
    },
    [exportConfig, exportFilename],
  );

  return { exportToExcel };
};

export default useExportExcel;
